/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
    Injectable
} from '@angular/core';
import {GlobalSearchCriterionFilterModel} from '../models/global-search-criterion-filter.model';
import {SearchCriteriaConfiguration} from '../configuration/search-criteria-configuration';
import {GlobalSearchCriteriaConfigurationMapper} from '../mappers/global-search-criteria-configuration.mapper';
import {
    DropdownCriterionSelectionModel
} from '../dropdown-criteria-selection/models/dropdown-criterion-selection.model';
import {QueryChipModel} from '../query-chips-area/models/query-chip.model';
import {DropdownCriteriaSelectionModel} from '../dropdown-criteria-selection/models/dropdown-criteria-selection.model';
import {QueryChipsAreaModel} from '../query-chips-area/models/query-chips-area.model';
import {BehaviorSubject} from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class GlobalSearchCriteriaService {
    private dropdownDataBySearchCriteriaCode: DropdownCriteriaSelectionModel;
    private selectedSearchCriterionFilter: GlobalSearchCriterionFilterModel;
    private selectedSearchCriterionName: string;
    private queryChipsArea: QueryChipsAreaModel = {
        criteriaFilters: []
    };
    queryChipsAreaWidth = new BehaviorSubject<number>(0);

    setDropdownDataBySearchCriteriaCode(searchCriteriaConfiguration: readonly SearchCriteriaConfiguration[]): void {
        this.dropdownDataBySearchCriteriaCode = GlobalSearchCriteriaConfigurationMapper
                .fromSearchCriteriaConfigurationToDropdownModelHashmap(searchCriteriaConfiguration);
    }

    getDropdownDataBySearchCriteriaCode(): DropdownCriteriaSelectionModel {
        return this.dropdownDataBySearchCriteriaCode;
    }

    getSelectedSearchCriterionFilter(): GlobalSearchCriterionFilterModel {
        return this.selectedSearchCriterionFilter;
    }

    setSelectedSearchCriterionFilter(filter: GlobalSearchCriterionFilterModel): void {
        this.selectedSearchCriterionFilter = filter;
    }

    getSelectedSearchCriterionName(): string {
        return this.selectedSearchCriterionName;
    }

    setSelectedSearchCriterionName(value: string): void {
        this.selectedSearchCriterionName = value;
    }

    hasSelectedFilter(): boolean {
        return this.selectedSearchCriterionFilter != null ? true : false;
    }

    addCriterionFilterToQuery(criterionFilter: QueryChipModel): void {
        this.queryChipsArea.criteriaFilters.push(criterionFilter);
        this.disableCriterion({
            criterion: criterionFilter.filter.criterion,
            criterionContext: criterionFilter.filter.criterionContext,
            criterionDynamicContext: criterionFilter.filter.criterionDynamicContext,
            criterionFilterInputType: criterionFilter.filter.criterionFilterInputType
        });
    }

    getCriteriaFiltersInQuery(): QueryChipModel[] {
        return this.queryChipsArea.criteriaFilters;
    }

    hasQueryCriteriaFilters(): boolean {
        return this.queryChipsArea.criteriaFilters.length > 0;
    }

    removeCriterionFilterFromQuery(criteriaFilter: QueryChipModel): void {
        this.queryChipsArea = {
            criteriaFilters: this.queryChipsArea.criteriaFilters.filter((criteria) => criteria !== criteriaFilter)
        };
    }

    removeAllCriteriaFiltersFromQuery(enableCriteria = false): void {
        this.queryChipsArea.criteriaFilters
                .forEach((chip) => {
                    this.removeCriterionFilterFromQuery(chip);
                    if (enableCriteria) {
                        this.enableCriterion(chip.filter);
                    }
                });
    }

    disableCriterion(filterModel: GlobalSearchCriterionFilterModel): void {
        this.criterionDisablerHandler(filterModel, true);
    }

    enableCriterion(filterModel: GlobalSearchCriterionFilterModel): void {
        this.criterionDisablerHandler(filterModel, false);
    }

    getCriterionFromSearchCriteria(criterionContextCode: string, criterionCode: string): DropdownCriterionSelectionModel {
        const criterionContext = this.dropdownDataBySearchCriteriaCode?.criteria.get(criterionContextCode);
        return criterionContext?.subDropdown?.get(criterionCode);
    }

    removeSelectedCriterion(): void {
        const hasSelectedSearchCriterion = this.getSelectedSearchCriterionFilter();
        if (hasSelectedSearchCriterion) {
            this.enableCriterion({
                criterionContext: hasSelectedSearchCriterion.criterionContext,
                criterion: hasSelectedSearchCriterion.criterion,
                criterionDynamicContext: hasSelectedSearchCriterion.criterionDynamicContext,
                criterionFilterInputType: hasSelectedSearchCriterion.criterionFilterInputType
            });
        }
    }

    private criterionDisablerHandler(filterModel: GlobalSearchCriterionFilterModel, disable: boolean): void {
        const criterion = this.getCriterionFromSearchCriteria(filterModel.criterionContext, filterModel.criterion);
        if (criterion) {
            criterion.disabled = disable;
        }
    }
}
